import { useState } from 'react';
import { format, isToday } from 'date-fns';
import styled from 'styled-components';

import { MonthDayCellSkeleton } from '../skeletons';

import DayPatientsModal from './DayPatientsModal';
import MonthViewAggregateCard from './MonthViewAggregateCard';

interface DayCellProps {
  date: Date;
  isOutsideMonth: boolean;
  isLoading: boolean;
  counts: { medicare: number; other: number };
}

export default function DayCell({ date, isOutsideMonth, isLoading, counts }: DayCellProps) {
  const [showModal, setShowModal] = useState(false);

  const dayNumber = format(date, 'd');
  const isCurrentDay = isToday(date);

  const total = counts.medicare + counts.other;
  const hasPatients = total > 0;

  const handleOpenModal = () => {
    if (hasPatients) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <Container $isOutsideMonth={isOutsideMonth} $clickable={hasPatients} onClick={handleOpenModal} data-cy='day-cell'>
        <DayNumberWrapper>
          <DayNumber $isToday={isCurrentDay} $isOutsideMonth={isOutsideMonth}>
            {dayNumber}
          </DayNumber>
        </DayNumberWrapper>
        <ContentArea>
          {isLoading ? (
            <MonthDayCellSkeleton />
          ) : (
            <>
              {counts.medicare > 0 && <MonthViewAggregateCard count={counts.medicare} payerType='medicare' />}
              {counts.other > 0 && <MonthViewAggregateCard count={counts.other} payerType='other' />}
            </>
          )}
        </ContentArea>
      </Container>
      {showModal && <DayPatientsModal date={date} totalCount={total} onClose={handleCloseModal} />}
    </>
  );
}

const Container = styled.div<{ $isOutsideMonth: boolean; $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 100px;
  min-width: 0;
  padding: 8px;
  border-right: 1px solid ${({ theme }) => theme.colors.black10};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  background-color: ${({ $isOutsideMonth, theme }) => ($isOutsideMonth ? theme.colors.black05 : theme.colors.white)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background-color 0.2s ease;

  &:nth-child(7n) {
    border-right: none;
  }

  &:hover {
    background-color: ${({ $clickable, $isOutsideMonth, theme }) => {
      if (!$clickable) return $isOutsideMonth ? theme.colors.black05 : theme.colors.white;
      return $isOutsideMonth ? theme.colors.black10 : theme.colors.black05;
    }};
  }
`;

const DayNumberWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const DayNumber = styled.span<{ $isToday: boolean; $isOutsideMonth: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  font-size: 12px;
  font-weight: ${({ $isToday }) => ($isToday ? 'var(--font-weight-bold)' : 'var(--font-weight-medium)')};
  color: ${({ $isOutsideMonth, $isToday, theme }) => {
    if ($isToday) return theme.colors.white;
    if ($isOutsideMonth) return theme.colors.black50;
    return theme.colors.black;
  }};
  background-color: ${({ $isToday, theme }) => ($isToday ? theme.colors.primaryBlue : 'transparent')};
  border-radius: 50%;
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;
